/**
 * Macro Tree Types
 *
 * Persisted data model for the stateful macro system. Each campaign that opts
 * in (`HostCampaignConfig.useStatefulMacroSystem === true`) can author one
 * `MacroTreeGraph` per supported `MacroTrigger`. v1 only allows the `'launch'`
 * trigger; the schema is intentionally trigger-agnostic so v2 can extend the
 * enum without protocol churn.
 *
 * See docs/feature-plans/stateful-macro-system/EXECUTION_PLAN_v2.md for the
 * full architectural context.
 */

import { z } from 'zod';

// ============================================================================
// TRIGGER + FAILURE ENUMS (forward-compatible)
// ============================================================================

/**
 * Which campaign-launch event a macro tree drives.
 * v1 supports only `'launch'`. v2 extends with `'join' | 'exit' | 'inactive'`
 * and the host coordinator wires the corresponding events.
 */
export const macroTriggerSchema = z.enum(['launch']);
export type MacroTrigger = z.infer<typeof macroTriggerSchema>;

/**
 * Per-node failure response when no child variant matches.
 * v1 supports only `'restart'` (full game relaunch from root, retry-budgeted).
 * v2 may add `'fail_immediately'` (skip retries, switch immediately) and
 * `'fall_through_to_legacy'` (defer to the campaign's legacy onLaunchMacro).
 */
export const macroOnFailureSchema = z.enum(['restart']);
export type MacroOnFailure = z.infer<typeof macroOnFailureSchema>;

// ============================================================================
// NODE KIND (definitive start / step / end markers)
// ============================================================================

/**
 * Explicit role of a node in the walk graph.
 *
 *  - `'start'` — the unique node the walk begins from. The API picks this
 *     node when the host sends `currentNodeId === null` on the very first
 *     MACRO_WALK_REQUEST. Exactly one `'start'` node per tree, and its id
 *     MUST equal `rootNodeId` (the redundancy is intentional — admin tools
 *     and the walk algorithm read different fields, and the validation
 *     refinement below pins them together).
 *  - `'step'` — an intermediate node. Has at least one outgoing edge. The
 *     default for any newly authored node.
 *  - `'end'` — terminal node. The API responds `outcome: 'complete'` when
 *     the host's captures match an `'end'` child. End nodes MUST have no
 *     outgoing edges (validated below).
 *
 * Migration: legacy persisted trees written before this field existed are
 * migrated on read by the realtime-api's `macroTreeService` (root → 'start',
 * leaves → 'end', else 'step'). Admin-dashboard's `MacroTreeTab` performs
 * the same fill-in client-side so the editor never sees missing kinds.
 */
export const macroNodeKindSchema = z.enum(['start', 'step', 'end']);
export type MacroNodeKind = z.infer<typeof macroNodeKindSchema>;

/**
 * Maximum allowed value for `MacroGroup.postMacroDelayMs` (60 seconds).
 * Generous enough for any realistic settle-time, low enough that an
 * accidental keystroke can't strand a host for hours.
 */
export const MACRO_POST_MACRO_DELAY_MAX_MS = 60_000;

// ============================================================================
// REGION (v1 stub, ignored at runtime — C++ TODO is the v2 hook)
// ============================================================================

export const macroRegionSchema = z.object({
  x: z.number().int().nonnegative(),
  y: z.number().int().nonnegative(),
  width: z.number().int().positive(),
  height: z.number().int().positive(),
});
export type MacroRegion = z.infer<typeof macroRegionSchema>;

// ============================================================================
// SNAPSHOT (one reference frame for a node's expected screen state)
// ============================================================================

/**
 * One reference screenshot for a node. A node may carry multiple snapshots
 * captured at different game-states (e.g. animation frames of a title screen);
 * the API matches a child if ANY of its snapshots matches the host-captured
 * hash within the child's hammingDistance threshold.
 *
 * `width`/`height` are extracted from the uploaded image at admin-dash time
 * and travel per-snapshot so a single node may carry frames at multiple
 * resolutions if needed.
 */
export const macroSnapshotSchema = z.object({
  /** Stable uuid v4. Primary key for the snapshot's S3 object. */
  id: z.string().uuid(),
  /** 16-char lowercase hex perceptual hash from computeMacroImageHash. */
  hash: z.string().regex(/^[0-9a-f]{16}$/),
  /** Capture resolution (extracted from the source image at upload time). */
  width: z.number().int().positive(),
  height: z.number().int().positive(),
  /** S3 url for the preview shown in the editor. */
  previewUrl: z.string().url(),
});
export type MacroSnapshot = z.infer<typeof macroSnapshotSchema>;

// ============================================================================
// MACRO GROUP (a node in the tree — one game-state plus its transition macros)
// ============================================================================

/**
 * A single node in the macro tree. Carries:
 *   - the macros to run when entering this node (existing key=/string=/gpad= syntax),
 *   - per-node failure behavior (`onFailure`),
 *   - and (optionally) the expected screen state used by its parent to identify
 *     us as the matched child via perceptual-hash comparison.
 *
 * The root node typically has no `expectedState` (nothing has happened yet to
 * identify it against); leaves are nodes with no outbound edges.
 */
export const macroGroupSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1).max(80),
  position: z.object({ x: z.number(), y: z.number() }),
  /** Macro steps in existing 'key=121@100^20' / 'string=foo@200' / 'gpad=A@100^20' format. */
  macros: z.array(z.string()),
  /** What the host should do when no child variant matches. v1: 'restart'. */
  onFailure: macroOnFailureSchema,
  /**
   * Definitive role in the walk graph. See `macroNodeKindSchema` for the
   * full contract. New nodes default to `'step'`; the unique `'start'` node
   * is also referenced by `MacroTreeGraph.rootNodeId` (the validation
   * refinement on `macroTreeGraphSchema` enforces the invariant).
   */
  kind: macroNodeKindSchema,
  /**
   * Milliseconds to wait after running this node's `macros` before requesting
   * the next screenshot from Playjector. Lets the admin pad in a short
   * settle delay for animations / fade-ins that would otherwise be captured
   * mid-transition. 0 = no delay (the default).
   *
   * Travels in `MacroWalkResponsePayload.postMacroDelayMs` so the host
   * (which executes the wait) doesn't need to know the tree shape.
   */
  postMacroDelayMs: z.number().int().min(0).max(MACRO_POST_MACRO_DELAY_MAX_MS).default(0),
  expectedState: z
    .object({
      /** Maximum hamming distance (0..64) for any snapshot to count as a match. */
      hammingDistance: z.number().int().min(0).max(64),
      /** At least one reference snapshot. The API matches the captured hash against any. */
      snapshots: z.array(macroSnapshotSchema).min(1),
      /** v1 stub — optional everywhere, ignored at runtime by Playjector. */
      region: macroRegionSchema.optional(),
    })
    .optional(),
});
export type MacroGroup = z.infer<typeof macroGroupSchema>;

// ============================================================================
// EDGE (parent → child relationship)
// ============================================================================

export const macroTreeEdgeSchema = z.object({
  id: z.string().uuid(),
  source: z.string().uuid(),
  target: z.string().uuid(),
});
export type MacroTreeEdge = z.infer<typeof macroTreeEdgeSchema>;

// ============================================================================
// FORK (UI-only sidecar — does NOT affect the walk)
// ============================================================================

/**
 * A fork point in the editor canvas. Forks are PURELY a visual cleanup
 * affordance: they do NOT exist in the walk algorithm and are NOT referenced
 * by `MacroTreeGraph.edges`. The canvas expands every fork into the cartesian
 * product of (ultimate-parents × ultimate-children) direct group→group edges
 * at save time, and the expanded edges are what the API/host walk.
 *
 * The editor stores forks in `metadata.forks` solely so the same clean
 * topology can be re-rendered when the admin reopens the tree. `inputs`
 * and `outputs` may reference either group ids OR other fork ids (chained
 * fan-out/fan-in is allowed in the editor).
 *
 * Walk-side consumers (MacroWalkHandler, StatefulMacroCoordinator) MUST NOT
 * read this field — it is exclusively for the admin-dashboard editor.
 */
export const macroForkSchema = z.object({
  id: z.string().uuid(),
  position: z.object({ x: z.number(), y: z.number() }),
  inputs: z.array(z.string().uuid()),
  outputs: z.array(z.string().uuid()),
});
export type MacroFork = z.infer<typeof macroForkSchema>;

// ============================================================================
// TREE GRAPH (entire macro tree for one (campaign, trigger) pair)
// ============================================================================
//
// Two flavors live here:
//
//   • macroTreeGraphSchema       — STRICT (used by the LOCKED slot). The
//                                   walk handler and Lock action both go
//                                   through this; the superRefine block
//                                   enforces walk-readiness invariants.
//   • macroTreeGraphDraftSchema  — PERMISSIVE (used by the DRAFT slot).
//                                   Authors can persist any in-progress
//                                   topology — empty trees, missing root,
//                                   orphan edges, no 'start' node, etc.
//                                   The walk handler NEVER reads from the
//                                   draft slot, so structural-only checks
//                                   are sufficient.
//

const macroTreeGraphBaseSchema = z.object({
  /** Which trigger this tree drives. v1: always 'launch'. */
  trigger: macroTriggerSchema,
  /** Walk starts here. Must reference a node in `nodes` for the strict schema; may be empty in drafts. */
  rootNodeId: z.string().uuid(),
  nodes: z.array(macroGroupSchema).min(1),
  edges: z.array(macroTreeEdgeSchema),
  metadata: z.object({
    version: z.string(),
    createdAt: z.number().int().nonnegative(),
    updatedAt: z.number().int().nonnegative(),
    /** UI-only sidecar (see `macroForkSchema`). Optional for forward/backward compat. */
    forks: z.array(macroForkSchema).optional(),
  }),
});

/**
 * Permissive base for DRAFT trees. Differs from the strict base only in:
 *  - `rootNodeId` may be the empty string (no root selected yet).
 *  - `nodes` may be empty (clean editor canvas).
 *
 * Everything else still has to be type-correct (Zod still rejects
 * malformed JSON, wrong field types, etc.) so a draft round-trips cleanly
 * and renders without runtime crashes.
 */
const macroTreeGraphDraftBaseSchema = macroTreeGraphBaseSchema.extend({
  rootNodeId: z.string(),
  nodes: z.array(macroGroupSchema),
});

/**
 * Returns true iff the directed graph defined by (nodeIds, edges) is acyclic.
 * Implemented via Kahn's algorithm — O(N + E).
 */
function isAcyclic(nodeIds: ReadonlyArray<string>, edges: ReadonlyArray<MacroTreeEdge>): boolean {
  const inDegree = new Map<string, number>();
  for (const id of nodeIds) inDegree.set(id, 0);
  for (const edge of edges) {
    inDegree.set(edge.target, (inDegree.get(edge.target) ?? 0) + 1);
  }
  const queue: string[] = [];
  for (const [id, deg] of inDegree.entries()) {
    if (deg === 0) queue.push(id);
  }
  let processed = 0;
  while (queue.length > 0) {
    const id = queue.shift()!;
    processed++;
    for (const edge of edges) {
      if (edge.source !== id) continue;
      const next = (inDegree.get(edge.target) ?? 0) - 1;
      inDegree.set(edge.target, next);
      if (next === 0) queue.push(edge.target);
    }
  }
  return processed === nodeIds.length;
}

/**
 * Returns true iff every node is reachable from `rootNodeId` via the directed
 * edges (source → target). BFS — O(N + E).
 */
function allNodesReachableFromRoot(
  rootNodeId: string,
  nodeIds: ReadonlyArray<string>,
  edges: ReadonlyArray<MacroTreeEdge>,
): boolean {
  const reachable = new Set<string>([rootNodeId]);
  const queue: string[] = [rootNodeId];
  while (queue.length > 0) {
    const current = queue.shift()!;
    for (const edge of edges) {
      if (edge.source !== current) continue;
      if (reachable.has(edge.target)) continue;
      reachable.add(edge.target);
      queue.push(edge.target);
    }
  }
  return nodeIds.every(id => reachable.has(id));
}

export const macroTreeGraphSchema = macroTreeGraphBaseSchema
  .superRefine((graph, ctx) => {
    const nodeIds = graph.nodes.map(n => n.id);
    const seen = new Set<string>();
    for (const id of nodeIds) {
      if (seen.has(id)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: `duplicate node id: ${id}`, path: ['nodes'] });
        return;
      }
      seen.add(id);
    }
    const edgeIds = graph.edges.map(e => e.id);
    const seenEdges = new Set<string>();
    for (const id of edgeIds) {
      if (seenEdges.has(id)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: `duplicate edge id: ${id}`, path: ['edges'] });
        return;
      }
      seenEdges.add(id);
    }
    if (!seen.has(graph.rootNodeId)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'rootNodeId must reference a node in nodes',
        path: ['rootNodeId'],
      });
      return;
    }
    for (const edge of graph.edges) {
      if (!seen.has(edge.source) || !seen.has(edge.target)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `edge ${edge.id} references unknown node`,
          path: ['edges'],
        });
        return;
      }
    }
    for (const node of graph.nodes) {
      if (!node.expectedState) continue;
      const snapIds = new Set<string>();
      for (const snap of node.expectedState.snapshots) {
        if (snapIds.has(snap.id)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `duplicate snapshot id ${snap.id} in node ${node.id}`,
            path: ['nodes'],
          });
          return;
        }
        snapIds.add(snap.id);
      }
    }
    // Forks live in metadata as a UI sidecar, but their ids are referenced by
    // other forks' `inputs`/`outputs` for chained fan-out/fan-in. Duplicates
    // would make those references ambiguous and break editor reconstruction.
    if (graph.metadata.forks) {
      const forkIds = new Set<string>();
      for (const fork of graph.metadata.forks) {
        if (forkIds.has(fork.id)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `duplicate fork id: ${fork.id}`,
            path: ['metadata', 'forks'],
          });
          return;
        }
        forkIds.add(fork.id);
      }
    }
    if (!isAcyclic(nodeIds, graph.edges)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'graph must be acyclic', path: ['edges'] });
      return;
    }
    if (!allNodesReachableFromRoot(graph.rootNodeId, nodeIds, graph.edges)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'every node must be reachable from rootNodeId',
        path: ['edges'],
      });
      return;
    }

    // Node-kind invariants. Authored explicitly per node; the legacy migrate-
    // on-read path in macroTreeService (admin-dashboard mirrors it) fills in
    // sensible defaults for trees written before this schema existed.
    const startNodes = graph.nodes.filter(n => n.kind === 'start');
    const endNodes = graph.nodes.filter(n => n.kind === 'end');
    if (startNodes.length !== 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `expected exactly 1 node with kind === 'start' (got ${startNodes.length})`,
        path: ['nodes'],
      });
      return;
    }
    if (startNodes[0].id !== graph.rootNodeId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "the 'start' node's id must equal rootNodeId",
        path: ['rootNodeId'],
      });
      return;
    }
    if (endNodes.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "expected at least 1 node with kind === 'end'",
        path: ['nodes'],
      });
      return;
    }
    // A leaf is a node with no outgoing edges. Every leaf MUST be 'end',
    // and conversely every 'end' MUST be a leaf. Together these mean the
    // walk algorithm can use `kind === 'end'` as a definitive terminal
    // signal without falling back to topology inspection.
    const outDegree = new Map<string, number>();
    for (const id of nodeIds) outDegree.set(id, 0);
    for (const edge of graph.edges) {
      outDegree.set(edge.source, (outDegree.get(edge.source) ?? 0) + 1);
    }
    for (const node of graph.nodes) {
      const isLeaf = (outDegree.get(node.id) ?? 0) === 0;
      if (isLeaf && node.kind !== 'end') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `node ${node.id} has no outgoing edges but kind !== 'end'`,
          path: ['nodes'],
        });
        return;
      }
      if (node.kind === 'end' && !isLeaf) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `node ${node.id} is kind: 'end' but has outgoing edges`,
          path: ['nodes'],
        });
        return;
      }
    }
  });
export type MacroTreeGraph = z.infer<typeof macroTreeGraphSchema>;

/**
 * Permissive schema for the DRAFT slot. NO superRefine — authors can save
 * a half-finished graph (empty, missing root, no end node, leaves with
 * kind: 'step', orphan edge refs, cycles) and reload it intact. The Lock
 * action re-validates against `macroTreeGraphSchema` before promoting a
 * draft to the locked slot.
 *
 * Walk-side consumers MUST NOT read from drafts; the realtime-api walk
 * handler is wired to the locked slot only.
 */
export const macroTreeGraphDraftSchema = macroTreeGraphDraftBaseSchema;
export type MacroTreeGraphDraft = z.infer<typeof macroTreeGraphDraftSchema>;
